import React, { useEffect, useState, useMemo } from "react";
import ReactDOM from "react-dom/client";
import sodium from "libsodium-wrappers-sumo";
import { v4 as uuid } from "uuid";
import "./styles.css";

// --- API ---
const API = "https://agem.jcdev.top/proxy.php?path=/api";

const sendMessage = (data) =>
    fetch(`${API}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
    });

const fetchMessages = (tag) =>
    fetch(`${API}/messages/${tag}`).then((r) => r.json());

const registerUser = (address, username) =>
    fetch(`${API}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address, username }),
    });

const fetchUsers = () =>
    fetch(`${API}/users`).then((r) => r.json());

// --- CRYPTO ---
let cryptoReady = false;

async function initCrypto() {
    if (!cryptoReady) {
        await sodium.ready;
        cryptoReady = true;
    }
}

function ensureCryptoReady() {
    if (!cryptoReady) throw new Error("Crypto not ready");
}

function createGroupKey() {
    ensureCryptoReady();
    return sodium.randombytes_buf(32);
}

function groupTag(key) {
    ensureCryptoReady();
    return sodium.to_hex(sodium.crypto_generichash(8, "group-tag", key));
}

function encryptMessage(key, text) {
    ensureCryptoReady();
    const nonce = sodium.randombytes_buf(
        sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES
    );

    const cipher = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
        text, null, null, nonce, key
    );

    return {
        ciphertext: sodium.to_base64(cipher),
        nonce: sodium.to_base64(nonce),
    };
}

function decryptMessage(key, cipher, nonce) {
    ensureCryptoReady();
    try {
        const msg = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
            null,
            sodium.from_base64(cipher),
            null,
            sodium.from_base64(nonce),
            key
        );
        return sodium.to_string(msg);
    } catch {
        return null;
    }
}

// --- STORAGE ---
function saveGroups(groups) {
    localStorage.setItem("groups", JSON.stringify(groups));
}

function loadGroups() {
    return JSON.parse(localStorage.getItem("groups") || "[]");
}

// --- COMPONENTS ---

function Modal({ title, children, onClose }) {
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{title}</h3>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {children}
                </div>
            </div>
        </div>
    );
}

function PromptModal({ title, description, initialValue = "", placeholder = "", onConfirm, onCancel }) {
    const [value, setValue] = useState(initialValue);

    const handleConfirm = () => {
        onConfirm(value);
    };

    return (
        <Modal title={title} onClose={onCancel}>
            <div className="prompt-modal">
                {description && <p className="description">{description}</p>}
                <input
                    autoFocus
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleConfirm();
                        if (e.key === "Escape") onCancel();
                    }}
                />
                <div className="modal-actions" style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <button onClick={onCancel}>Cancel</button>
                    <button className="primary" onClick={handleConfirm}>Confirm</button>
                </div>
            </div>
        </Modal>
    );
}

function Avatar({ address, size = 40 }) {
    const { bg, rects } = useMemo(() => {
        if (!address) return { bg: '#eee', rects: [] };

        let seed = address.toLowerCase().trim();
        if (seed.startsWith('0x')) seed = seed.slice(2);

        const hashBytes = sodium.crypto_hash_sha256(seed);
        const hash = sodium.to_hex(hashBytes);

        const rBg = parseInt(hash.substring(0, 2), 16);
        const gBg = parseInt(hash.substring(2, 4), 16);
        const bBg = parseInt(hash.substring(4, 6), 16);
        const bgColor = `rgb(${rBg}, ${gBg}, ${bBg})`;

        const rFg = parseInt(hash.substring(6, 8), 16);
        const gFg = parseInt(hash.substring(8, 10), 16);
        const bFg = parseInt(hash.substring(10, 12), 16);
        const fgColor = `rgb(${rFg}, ${gFg}, ${bFg})`;

        const blocks = 5;
        const blockSize = size / blocks;
        const rectangles = [];

        let hashIndex = 0;

        for (let y = 0; y < blocks; y++) {
            for (let x = 0; x < Math.ceil(blocks / 2); x++) {
                const char = hash[hashIndex] || '0';
                const val = parseInt(char, 16);
                const bit = val % 2;
                hashIndex++;

                if (bit === 1) {
                    rectangles.push(
                        <rect
                            key={`l-${x}-${y}`}
                            x={x * blockSize}
                            y={y * blockSize}
                            width={blockSize + 0.5}
                            height={blockSize + 0.5}
                            fill={fgColor}
                        />
                    );

                    const mirrorX = blocks - 1 - x;
                    if (mirrorX !== x) {
                        rectangles.push(
                            <rect
                                key={`r-${mirrorX}-${y}`}
                                x={mirrorX * blockSize}
                                y={y * blockSize}
                                width={blockSize + 0.5}
                                height={blockSize + 0.5}
                                fill={fgColor}
                            />
                        );
                    }
                }
            }
        }

        return { bg: bgColor, rects: rectangles };
    }, [address, size]);

    return (
        <div
            className="avatar-icon"
            style={{
                width: size,
                height: size,
                minWidth: size,
                borderRadius: '50%',
                overflow: 'hidden',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: bg,
                border: '1px solid rgba(255,255,255,0.1)'
            }}
        >
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} shapeRendering="crispEdges">
                <rect width={size} height={size} fill={bg} />
                {rects}
            </svg>
        </div>
    );
}

function GroupManager({ groups, activeId, onSelect, onAdd, onRename, onDelete }) {
    const [showPrompt, setShowPrompt] = useState(null);

    const handleCreateGroup = () => {
        setShowPrompt({
            title: "New Group",
            description: "Enter a name for the new group:",
            placeholder: "e.g., Secret Project",
            onConfirm: async (name) => {
                if (!name || name.trim() === "") return;
                await sodium.ready;
                const key = createGroupKey();
                const tag = groupTag(key);

                onAdd({
                    id: uuid(),
                    name: name.trim(),
                    key: sodium.to_base64(key),
                    tag,
                });
                setShowPrompt(null);
            }
        });
    };

    const handleJoinFromOTL = () => {
        setShowPrompt({
            title: "Join Group",
            description: "Paste the invite token below:",
            placeholder: "Invite Token...",
            onConfirm: async (token) => {
                if (!token) return;
                try {
                    await sodium.ready;
                    const inviteId = sodium.to_hex(
                        sodium.crypto_generichash(32, sodium.from_base64(token))
                    );

                    const res = await fetch(`${API}/invite/${inviteId}`);
                    if (!res.ok) return alert("Invite invalid or expired");

                    const { encrypted_key, nonce } = await res.json();

                    const kInvite = sodium.crypto_generichash(
                        32,
                        "invite-key",
                        sodium.from_base64(token)
                    );

                    const groupKey = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
                        null,
                        sodium.from_base64(encrypted_key),
                        null,
                        sodium.from_base64(nonce),
                        kInvite
                    );

                    onAdd({
                        id: uuid(),
                        name: "Joined Group " + (groups.length + 1),
                        key: sodium.to_base64(groupKey),
                        tag: groupTag(groupKey),
                    });
                    setShowPrompt(null);
                } catch (e) {
                    alert("Failed to join group. Invalid token or server error.");
                    console.error(e);
                }
            }
        });
    };

    const handleRenameClick = (e, group) => {
        e.stopPropagation();
        setShowPrompt({
            title: "Rename Group",
            description: "Enter new group name:",
            initialValue: group.name,
            onConfirm: (newName) => {
                if (newName && newName.trim() !== "") {
                    onRename(group.id, newName.trim());
                }
                setShowPrompt(null);
            }
        });
    };

    return (
        <div className="group-manager animated fadeIn">
            <div className="group-actions">
                <button className="primary" onClick={handleCreateGroup}>
                    ➕ Create
                </button>
                <button onClick={handleJoinFromOTL}>
                    🔗 Join
                </button>
            </div>

            <div className="group-list">
                {groups.length === 0 && (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: '0.9em' }}>
                        No groups yet.
                    </div>
                )}
                {groups.map((g) => (
                    <div
                        key={g.id}
                        className={`group-item ${g.id === activeId ? 'active' : ''}`}
                        onClick={() => onSelect(g)}
                    >
                        <span style={{ fontWeight: g.id === activeId ? '600' : '400', color: g.id === activeId ? '#fff' : 'var(--text-muted)' }}>
                            {g.name || "Untitled Group"}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <button
                                className="icon-btn"
                                style={{ width: 24, height: 24, padding: 2, fontSize: '0.8em', background: 'transparent', border: 'none', opacity: 0.7 }}
                                onClick={(e) => handleRenameClick(e, g)}
                                title="Rename Group"
                            >
                                ✏️
                            </button>
                            <button
                                className="icon-btn"
                                style={{ width: 24, height: 24, padding: 2, fontSize: '0.8em', background: 'transparent', border: 'none', opacity: 0.7, color: 'var(--danger)' }}
                                onClick={(e) => { e.stopPropagation(); onDelete(g.id); }}
                                title="Delete Group"
                            >
                                🗑️
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {showPrompt && (
                <PromptModal
                    {...showPrompt}
                    onCancel={() => setShowPrompt(null)}
                />
            )}
        </div>
    );
}

function Chat({ group, goBack, walletAddress, userMap = {} }) {
    const [messages, setMessages] = useState([]);
    const [inviteToken, setInviteToken] = useState(null);
    const [loadingInvite, setLoadingInvite] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");

    const groupKey = sodium.from_base64(group.key);

    useEffect(() => {
        const load = async () => {
            try {
                const data = await fetchMessages(group.tag);
                const decrypted = data
                    .map((m) => {
                        try {
                            const plain = decryptMessage(groupKey, m.ciphertext, m.nonce);
                            if (!plain) return null;

                            try {
                                const parsed = JSON.parse(plain);
                                if (parsed.text && parsed.sender) {
                                    return { ...parsed, type: 'json', id: m.id };
                                }
                                return { text: plain, sender: 'Unknown', type: 'text', id: m.id };
                            } catch {
                                return { text: plain, sender: 'Unknown', type: 'text', id: m.id };
                            }
                        } catch (e) {
                            return null;
                        }
                    })
                    .filter(Boolean);
                setMessages(decrypted);
            } catch (e) {
                console.error("Failed to load messages", e);
            }
        };

        load();
        const i = setInterval(load, 3000);
        return () => clearInterval(i);
    }, [group]);

    const handleSend = async (text) => {
        if (!text) return;

        const payload = JSON.stringify({
            text,
            sender: walletAddress
        });

        const { ciphertext, nonce } = encryptMessage(groupKey, payload);
        await sendMessage({ tag: group.tag, ciphertext, nonce });
    };

    const createInvite = async () => {
        setLoadingInvite(true);
        try {
            await sodium.ready;

            const token = sodium.randombytes_buf(32);
            const inviteId = sodium.to_hex(sodium.crypto_generichash(32, token));
            const kInvite = sodium.crypto_generichash(32, "invite-key", token);

            const nonce = sodium.randombytes_buf(
                sodium.crypto_aead_xchacha20poly1305_ietf_NPUBBYTES
            );

            const encryptedKey =
                sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
                    groupKey, null, null, nonce, kInvite
                );

            await fetch(`${API}/invite`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    inviteId,
                    encryptedKey: sodium.to_base64(encryptedKey),
                    nonce: sodium.to_base64(nonce),
                }),
            });

            setInviteToken(sodium.to_base64(token));
        } catch (e) {
            alert("Error creating invite");
            console.error(e);
        } finally {
            setLoadingInvite(false);
        }
    };

    const copyToken = () => {
        navigator.clipboard.writeText(inviteToken);
        alert("Token copied to clipboard!");
    };

    return (
        <>
            <div className="chat">
                <div className="chat-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <button className="icon-btn" onClick={goBack}>⬅</button>
                        <h2>{group.name || 'Chat Group'}</h2>
                    </div>

                    <button onClick={createInvite} disabled={loadingInvite}>
                        {loadingInvite ? "Creating..." : "🔗 Share Invite"}
                    </button>
                </div>

                <div className="messages">
                    {messages.length === 0 && (
                        <div style={{ textAlign: 'center', marginTop: 50, color: 'var(--text-muted)' }}>
                            No messages yet. Start the conversation!
                        </div>
                    )}
                    {messages.map((m, i) => {
                        const isMe = m.sender?.toLowerCase() === walletAddress?.toLowerCase();
                        const senderAddr = m.sender !== 'Unknown' ? m.sender : '0x000000';

                        return (
                            <div key={m.id || i} className={`msg-row ${isMe ? 'me' : 'other'}`}>
                                {!isMe && (
                                    <div className="msg-avatar">
                                        <Avatar address={senderAddr} size={32} />
                                    </div>
                                )}
                                <div className={`msg-content ${isMe ? 'me' : 'other'}`}>
                                    {!isMe && m.sender !== 'Unknown' && (
                                        <span className="sender-label">
                                            {userMap[m.sender] || m.sender.slice(0, 6) + '...' + m.sender.slice(-4)}
                                        </span>
                                    )}
                                    {m.text}
                                </div>
                                {isMe && (
                                    <div className="msg-avatar">
                                        <Avatar address={walletAddress} size={32} />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="input-area">
                    <input
                        placeholder="Type a secure message..."
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleSend(e.target.value);
                                e.target.value = "";
                            }
                        }}
                    />
                </div>
            </div>

            {inviteToken && (
                <Modal title="Invite Created" onClose={() => setInviteToken(null)}>
                    <p style={{ color: 'var(--text-muted)', marginBottom: 10 }}>
                        Share this one-time token with a friend. It allows them to join this secure group.
                    </p>
                    <div className="token-box">
                        {inviteToken}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                        <button onClick={() => setInviteToken(null)}>Close</button>
                        <button className="primary" onClick={copyToken}>Copy Token</button>
                    </div>
                </Modal>
            )}
        </>
    );
}

function App() {
    const [groups, setGroups] = useState([]);
    const [active, setActive] = useState(null);
    const [ready, setReady] = useState(false);
    const [walletAddress, setWalletAddress] = useState(null);
    const [userMap, setUserMap] = useState({});
    const [showPrompt, setShowPrompt] = useState(null);

    const updateUserMap = async () => {
        try {
            const users = await fetchUsers();
            const map = {};
            users.forEach(u => map[u.address] = u.username);
            setUserMap(map);
        } catch (e) {
            console.error("Failed to fetch users");
        }
    };

    useEffect(() => {
        const init = async () => {
            await initCrypto();
            setReady(true);
            setGroups(loadGroups());
            updateUserMap();

            if (window.ethereum) {
                try {
                    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                    if (accounts.length > 0) {
                        setWalletAddress(accounts[0]);
                    }
                } catch (error) {
                    console.error("Error checking accounts", error);
                }
            }
        };

        init();

        const i = setInterval(updateUserMap, 10000);
        return () => clearInterval(i);
    }, []);

    const connectWallet = async () => {
        if (window.ethereum) {
            try {
                const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
                setWalletAddress(accounts[0]);
            } catch (error) {
                console.error("Error connecting wallet", error);
            }
        } else {
            alert("Please install MetaMask or another Ethereum wallet!");
        }
    };

    const renameGroup = (id, newName) => {
        const updated = groups.map(g => g.id === id ? { ...g, name: newName } : g);
        setGroups(updated);
        saveGroups(updated);
    };

    const handleSetUsername = () => {
        setShowPrompt({
            title: "Set Username",
            description: "Enter your public username:",
            initialValue: userMap[walletAddress] || "",
            onConfirm: async (name) => {
                if (name && name.trim()) {
                    try {
                        await registerUser(walletAddress, name.trim());
                        updateUserMap();
                    } catch (e) {
                        alert("Failed to register username");
                    }
                }
                setShowPrompt(null);
            }
        });
    };

    if (!ready) {
        return (
            <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#00f0ff' }}>
                Loading secure environment...
            </div>
        );
    }

    if (!walletAddress) {
        return (
            <div className="landing-page">
                <h1 className="logo">🔐 AGEM</h1>
                <div className="card landing-card">
                    <h2>Connect Your Wallet</h2>
                    <p>
                        To ensure secure identity and encrypted communication, you must connect your crypto wallet.
                        This wallet address will serve as your identity in the chat.
                    </p>
                    <button className="primary" onClick={connectWallet}>
                        🦊 Connect Wallet
                    </button>
                </div>
            </div>
        );
    }

    const addGroup = (group) => {
        const updated = [...groups, group];
        setGroups(updated);
        saveGroups(updated);
    };

    const deleteGroup = (id) => {
        if (window.confirm("Are you sure you want to delete this group?")) {
            const updated = groups.filter(g => g.id !== id);
            setGroups(updated);
            saveGroups(updated);
            if (active && active.id === id) {
                setActive(null);
            }
        }
    };

    return (
        <div className={`app ${active ? 'view-chat' : 'view-sidebar'}`}>
            <div className="sidebar">
                <div className="sidebar-header">
                    <h1 className="logo">🔐 AGEM</h1>
                    <div className="wallet-info">
                        <Avatar address={walletAddress} size={24} />
                        <span className="wallet-addr">
                            {userMap[walletAddress] || walletAddress.slice(0, 6) + '...' + walletAddress.slice(-4)}
                        </span>
                    </div>
                    <button
                        className="icon-btn"
                        style={{
                            marginTop: 10,
                            width: '100%',
                            borderRadius: 8,
                            height: 'auto',
                            background: 'rgba(255,255,255,0.05)'
                        }}
                        onClick={handleSetUsername}
                    >
                        ✏️ Set Username
                    </button>
                </div>

                <GroupManager
                    groups={groups}
                    activeId={active ? active.id : null}
                    onSelect={setActive}
                    onAdd={addGroup}
                    onRename={renameGroup}
                    onDelete={deleteGroup}
                />
            </div>

            <div className="main-content">
                {!active ? (
                    <div className="placeholder-screen">
                        <h2>Welcome, {userMap[walletAddress] || walletAddress.slice(0, 6) + '...' + walletAddress.slice(-4)}!</h2>
                        <p>Select a group from the sidebar to start chatting.</p>
                    </div>
                ) : (
                    <Chat
                        key={active.id}
                        group={active}
                        goBack={() => setActive(null)}
                        walletAddress={walletAddress}
                        userMap={userMap}
                    />
                )}
            </div>

            {showPrompt && (
                <PromptModal
                    {...showPrompt}
                    onCancel={() => setShowPrompt(null)}
                />
            )}
        </div>
    );
}

// --- RENDER ---
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
